/** The user shape both the auth button and the account page read from. */
export interface DisplayUser {
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

/**
 * Turns "ada.lovelace@example.com" into "Ada Lovelace" — a usable display name
 * when the token carries no `name` claim. Returns undefined for a blank email so
 * the caller's own fallback applies.
 */
export function prettyNameFromEmail(email?: string): string | undefined {
  const local = email?.split('@')[0]?.trim();
  if (!local) return undefined;
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * What to call the signed-in user.
 *
 * Central tokens always carry `email` and usually `name`; when only the email is
 * present the local-part is shown rather than the raw address, which would
 * otherwise be duplicated wherever the email is already displayed alongside.
 *
 * Shared so the account page and the header agree — two different renderings of
 * someone's own name is the kind of small wrongness people notice.
 */
export function resolveDisplayName(user: DisplayUser | null | undefined): string {
  return (
    user?.name?.trim() ||
    (user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.firstName || user?.lastName || prettyNameFromEmail(user?.email) || 'Your account')
  );
}

export function initialFor(user: DisplayUser | null | undefined): string {
  return resolveDisplayName(user).charAt(0).toUpperCase();
}
